import { Fragment, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { NoResults } from 'basic-components';
import { Query, ReduxService } from 'dashboard-services';
import { Fetching } from 'primary-components';

import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import { dataStorageQueries } from 'queries/datastorage';

import SymbolRow from './SymbolRow';

import { When } from 'react-if';
import InfiniteScroll from 'react-infinite-scroller';

import './Results.scss';

const emptyArray = []
const useSearchSymbols = ({ query, symbolsFilter, metadatasFilter, groupFilter, userDefinedFilters, setTotalSize, setSymbols, source }) => {
  const dispatch = useDispatch(),
        buildedQuery = creatorSeriesActions.getSearchQuery({ query, symbolsFilter, metadatasFilter, groupName: groupFilter }),
        sortBy = creatorSeriesActions.getSortString(userDefinedFilters),
        getQueryBuilder = useCallback(() => new Query.Builder(dispatch(getApiConfig()))
          .withFetching(false)
          .withShouldDisplayError(false)
          .withHeader("Target", source, true)
        , [dispatch, source]),
        { data: symbolsRaw, isPending, isFetchingNextPage, fetchNextPage, hasNextPage } = dataStorageQueries.useSearchSymbols({
          query: buildedQuery,
          getQueryBuilder,
          sortBy,
          size: 100,
          enabled: !!(query || Object.keys(metadatasFilter || {}).length > 0 || Object.keys(symbolsFilter).length > 0 || groupFilter),
          shouldRefetch: false,
          provider: source
        }),
        totalSize = useMemo(() => symbolsRaw?.pages[symbolsRaw?.pages?.length - 1]?.totalSize, [symbolsRaw?.pages]),
        symbols = useMemo(() => (symbolsRaw?.pages || emptyArray).flatMap(p => p?.items || emptyArray), [symbolsRaw?.pages])

  useEffect(() => {
    if(totalSize) {
      setTotalSize(totalSize)
    }
  }, [setTotalSize, totalSize])

  useEffect(() => {
    if(symbols) {
      setSymbols(symbols)
    }
  }, [setSymbols, symbols])

  return { isPending, isFetchingNextPage, fetchNextPage, hasNextPage, symbols, totalSize }
}

const Results = ({ metadatasFilter, symbolsFilter, groupFilter, getResultsStyle, setTotalSize, setSymbols }) => {
  const dispatch = useDispatch(),
        source = useSelector(state => state.globalState.source),
        { query, userDefinedFilters, selectedSymbols } = useSelector(state => state.seriesState.creatorState),
        { fetchNextPage, hasNextPage, isFetchingNextPage, isPending, symbols, totalSize } = useSearchSymbols({
          query,
          symbolsFilter,
          metadatasFilter,
          groupFilter,
          userDefinedFilters,
          setTotalSize,
          setSymbols,
          source,
        }),
        symbolsLength = useMemo(() => symbols?.length, [symbols]),
        arePatternsSelected = useMemo(() => selectedSymbols.some(s => s.pattern === "true" || s.pattern === undefined), [selectedSymbols]),
        addSymbol = useCallback(symbolIndex => {
          const symbol = symbols[symbolIndex]
          if(!symbol) {
            return;
          }
          dispatch(creatorSeriesActions.swapSelectedSymbol(ReduxService.updateObject(symbol, { pattern: "false", source })))
        }, [dispatch, symbols, source]),
        
        openBasket = useCallback(() => dispatch(creatorSeriesActions.onChange(true, "isBasketOpen")), [dispatch]),
        onRowClick = useCallback(s => dispatch(creatorSeriesActions.onAddRow({ symbol: s, source, selectedSymbols, addToBasket: addSymbol, arePatternsSelected })), [addSymbol, arePatternsSelected, dispatch, selectedSymbols, source]),
        resultsStyle = useMemo(() => getResultsStyle(), [getResultsStyle]),
        scrollContRef = useRef()

  return (
    <div 
        className="ng-office-app__authed__content__results"
        style={resultsStyle}
    >
      <div className="ng-office-app__authed__content__results__headers">
        <When condition={symbolsLength > 0}>
          {() => (
            <Fragment>
              {userDefinedFilters.map(udf => (
                <div 
                    className="ng-office-app__authed__content__results__headers__header"
                    key={udf.filterKey}
                >
                  {udf.type === creatorSeriesActions.FILTER_TYPE.GROUP_NAME ? "Group" : udf.label}
                </div>
              ))}
              {Object.keys(symbols[0]?.symbols || {}).filter(symbolKey => userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.SYMBOL && udf.filterKey === symbolKey) === -1).map(symbolKey => (
                <div 
                    className="ng-office-app__authed__content__results__headers__header"
                    key={symbolKey}
                >
                  {symbolKey}
                </div>
              ))}
              {Object.keys(symbols[0]?.metadata || {}).filter(metadataKey => userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.METADATA && udf.filterKey === metadataKey) === -1).map(metadataKey => (
                <div 
                    className="ng-office-app__authed__content__results__headers__header"
                    key={metadataKey}
                >
                  {metadataKey}
                </div>
              ))}
              <When condition={userDefinedFilters.findIndex(udf => udf.type === creatorSeriesActions.FILTER_TYPE.GROUP_NAME) === -1}>
                {() => (
                  <div className="ng-office-app__authed__content__results__headers__header">
                    Group
                  </div>
                )}
              </When>
            </Fragment>
          )}
        </When>
      </div>
      <div 
          className="ng-office-app__authed__content__results__body"
          ref={scrollContRef}
      >
        <Fetching isFetching={isPending || isFetchingNextPage}/>
        <InfiniteScroll
            className="ng-office-app__authed__content__results__body__scroll"
            hasMore={hasNextPage}
            initialLoad={false}
            loadMore={fetchNextPage}
            threshold={800}
            useWindow={false}
        >
          {symbols.map((symbol, symbolIndex) => {
            const { 
              isDirectlySelected,
              isPatternSelected
            } = creatorSeriesActions.isInBasket(symbol, selectedSymbols)
            return (
              <SymbolRow
                  {...symbol}
                  isPatternSelected={isPatternSelected}
                  isSelected={isDirectlySelected}
                  key={symbolIndex}
                  onRowClick={isPatternSelected && !isDirectlySelected ? openBasket : onRowClick}
                  scrollContRef={scrollContRef}
                  symbolIndex={symbolIndex}
                  userDefinedFilters={userDefinedFilters}
              />
            )
          })}
        </InfiniteScroll>
        <When condition={symbolsLength === 0 && (query || Object.keys(metadatasFilter || {}).length > 0 || Object.keys(symbolsFilter || {}).length > 0 || groupFilter) && (!isPending && !isFetchingNextPage)}>
          {() => (
            <NoResults/>
          )}
        </When>
      </div>
      <div className="ng-office-app__authed__content__results__footer">
        {isPending ? "" : `${Number(symbolsLength > totalSize ? totalSize : symbolsLength).toLocaleString("en-EN")} of ${Number(totalSize).toLocaleString("en-EN")}`}
      </div>
    </div>
  )
}

Results.defaultProps = {
  groupFilter: undefined,
  metadatasFilter: {},
  symbols: [],
  symbolsFilter: {},
  totalSize: undefined
}
Results.propTypes = {
  getResultsStyle: PropTypes.func.isRequired,
  groupFilter: PropTypes.string,
  metadatasFilter: PropTypes.object,
  setSymbols: PropTypes.func.isRequired,
  setTotalSize: PropTypes.func.isRequired,
  symbols: PropTypes.array,
  symbolsFilter: PropTypes.object,
  totalSize: PropTypes.number,
}

export default Results;